import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import Chart from "chart.js/auto";
import type { ChartConfiguration, ChartDataset } from "chart.js";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { SharedModule } from "../../../../shared/shared.module";
import { AideContextComponent } from "../../../../shared/components/aide-context/aide-context.component";
import { AideContextSection } from "../../../../shared/models/aide-context.model";
import { TranslateUtils } from "../../../../shared/utils/translate.utils";
import { LignesFacadeService } from "../../../ligne/services/lignes/lignes-facade.service";
import { UiFacadeService } from "../../../user_infos/services/store/ui-facade.service";
import { UserInfoCardComponent } from "../../../../shared/components/user-info-card/user-info-card.component";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { AutoSignificantDigitsPipe } from "../../../../shared/pipes/number-sig.pipe";
import { LoaderSkeletonComponent } from "../../../../shared/loader-skeleton/loader-skeleton.component";
import { Travail } from "../../../info-journaliere/models/travail.model";
import { OF } from "../../../of/models/of.model";

type ViewType = "PIE" | "LIST";

@Component({
  selector: "vl-widget-main-oeuvre-ligne",
  standalone: true,
  imports: [
    CommonModule,
    WidgetTitleComponent,
    ChipsToggleComponent,
    SharedModule,
    ChipsComponent,
    UserInfoCardComponent,
    AideContextComponent,
    AutoSignificantDigitsPipe,
    LoaderSkeletonComponent,
  ],
  templateUrl: "./widget-main-oeuvre-ligne.component.html",
  styleUrls: ["./widget-main-oeuvre-ligne.component.scss"],
  host: { class: "widget-component" },
})
export class WidgetMainOeuvreLigneComponent {
  private ligneFacade = inject(LignesFacadeService);
  private uiFacade = inject(UiFacadeService);
  private destroyRef = inject(DestroyRef);
  stats = this.ligneFacade.getState().stats;

  widget = input<Widget>();
  metadata = input<EntityMetadata>();

  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("moChart");
  chart?: Chart<"doughnut", number[], string>;

  aideSections: AideContextSection[] = [
    { type: "text", html: TranslateUtils.translate("stats_ligne.aide_main_oeuvre") },
    { type: "highlight", title: "Calcul", html: TranslateUtils.translate("stats_ligne.aide_main_oeuvre_formule") },
  ];

  viewType = signal<ViewType>("PIE");

  elementsView = [
    { icon: "pie_chart", value: "PIE" },
    { icon: "list", value: "LIST" },
  ];

  readonly topCount = 10;

  private parseDureeToMinutes(s: unknown): number {
    if (!s) return 0;
    const str = String(s).trim();
    let m = /^(\d{1,3})\s*h\s*(\d{1,2})?$/i.exec(str);
    if (m) return parseNumber(m[1] || 0) * 60 + parseNumber(m[2] || 0);
    m = /^(\d{1,3}):(\d{1,2})$/.exec(str);
    if (m) return parseNumber(m[1] || 0) * 60 + parseNumber(m[2] || 0);
    m = /^(\d+(?:[.,]\d+)?)$/.exec(str);
    if (m) return Math.round(parseNumber(m[1].replace(",", ".")) * 60);
    return 0;
  }

  formatMinutes(totalMin: number): string {
    const min = Math.max(0, Math.round(totalMin));
    const h = Math.floor(min / 60);
    const mm = String(min % 60).padStart(2, "0");
    return `${h}h${mm}`;
  }

  private travauxFiltres = computed(() => {
    const s = this.stats();
    const travaux = (s?.travail ?? []) as Travail[];
    const ofIds = new Set((s?.ofs ?? [] as OF[]).map((o) => o.id));
    if (!travaux?.length) return [];
    return travaux.filter((t) => !t.ofId || ofIds.has(t.ofId));
  });

  private getUserName(userInfosId: string): string {
    if (!userInfosId) return TranslateUtils.translate("stats_ligne.inconnu");
    const ui = this.uiFacade.getById(userInfosId)?.();
    if (!ui) return userInfosId;
    return [ui.prenom, ui.nom].filter(Boolean).join(" ") || userInfosId;
  }

  private baseRows = computed(() => {
    const map = new Map<string, number>();
    for (const t of this.travauxFiltres()) {
      const id = t.userInfosId || TranslateUtils.translate("stats_ligne.inconnu");
      map.set(id, (map.get(id) ?? 0) + this.parseDureeToMinutes(t.duree));
    }
    return Array.from(map.entries()).map(([userId, minutes]) => ({
      key: userId,
      label: this.getUserName(userId),
      minutes,
    }));
  });

  private sorted = computed(() => {
    const arr = [...this.baseRows()];
    arr.sort((a, b) => b.minutes - a.minutes);
    return arr;
  });

  maxRows = computed(() => this.baseRows().length);
  private top = computed(() => {
    const n = Math.max(1, Math.min(this.topCount, this.maxRows()));
    return this.sorted().slice(0, n);
  });

  private totalAll = computed(() =>
    this.baseRows().reduce((a, x) => a + x.minutes, 0),
  );
  private totalTopOnly = computed(() =>
    this.top().reduce((a, x) => a + x.minutes, 0),
  );
  private hasOthers = computed(() => this.baseRows().length > this.top().length);
  private othersValue = computed(() =>
    Math.max(0, this.totalAll() - this.totalTopOnly()),
  );

  values = computed(() => {
    const vals = this.top().map((x) => x.minutes);
    if (this.hasOthers()) vals.push(this.othersValue());
    return vals;
  });

  private pieKeys = computed(() => {
    const keys = this.top().map((x) => x.key);
    if (this.hasOthers()) keys.push(TranslateUtils.translate("stats_ligne.autres"));
    return keys;
  });

  private pieLabels = computed(() => {
    const labs = this.top().map((x) => x.label);
    if (this.hasOthers()) labs.push(TranslateUtils.translate("stats_ligne.autres"));
    return labs;
  });

  listRows = computed(() => {
    const topRows = this.top();
    const total = this.totalAll() || 1;
    const rows = topRows.map((x) => ({
      key: x.key,
      label: x.label,
      value: x.minutes,
      pct: (x.minutes / total) * 100,
    }));
    if (this.hasOthers()) {
      const othersMin = this.othersValue();
      rows.push({
        key: TranslateUtils.translate("stats_ligne.autres"),
        label: TranslateUtils.translate("stats_ligne.autres"),
        value: othersMin,
        pct: (othersMin / total) * 100,
      });
    }
    return rows;
  });

  private makeColors(n: number): string[] {
    const base = [140, 210, 260, 275, 40, 0, 195, 220];
    const out: string[] = [];
    for (let i = 0; i < n; i++) {
      const h =
        base[i % base.length] +
        (i >= base.length ? 12 * Math.floor(i / base.length) : 0);
      out.push(`hsl(${h % 360} 85% 55%)`);
    }
    return out;
  }

  private colors = computed(() => this.makeColors(this.values().length));

  legendLabels = computed(() => {
    const labels = this.pieLabels();
    const vals = this.values();
    const cols = this.colors();
    const keys = this.pieKeys();
    const total = vals.reduce((a, b) => a + b, 0);
    return labels.map((lbl, i) => ({
      key: keys[i] ?? lbl,
      label: lbl,
      percentage: total > 0 ? ((+vals[i] / total) * 100).toFixed(0) : "0",
      color: String(cols[i] ?? "#999"),
    }));
  });

  constructor() {
    this.destroyRef.onDestroy(() => this.destroyChart());

    // Single merged chart effect: build or update
    effect(
      () => {
        if (this.viewType() !== "PIE") {
          untracked(() => this.destroyChart());
          return;
        }
        const canvas = this.canvasRef();
        const values = this.values();
        const labels = this.pieLabels();
        const colors = this.colors();

        if (!canvas || !values.length) {
          untracked(() => this.destroyChart());
          return;
        }

        untracked(() => {
          if (this.chart) {
            const ds = this.chart.data.datasets[0] as ChartDataset<"doughnut", number[]>;
            this.chart.data.labels = labels;
            ds.data = values;
            ds.backgroundColor = colors;
            this.chart.update();
          } else {
            this.buildChart(canvas, labels, values, colors);
          }
        });
      },
    );
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private buildChart(
    canvas: ElementRef<HTMLCanvasElement>,
    labels: string[],
    values: number[],
    colors: string[],
  ) {
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;
    this.destroyChart();

    const config: ChartConfiguration<"doughnut", number[], string> = {
      type: "doughnut",
      data: {
        labels,
        datasets: [
          {
            label: TranslateUtils.translate("stats_ligne.heures"),
            data: values,
            backgroundColor: colors,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            display: true,
            position: "right",
            labels: {
              usePointStyle: true,
              generateLabels: () => [],
            },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${ctx.label}: ${this.formatMinutes(ctx.parsed)}`,
            },
          },
        },
      },
    };
    this.chart = new Chart<"doughnut", number[], string>(ctx, config);
  }

  protected readonly Math = Math;
}
